//#region Services
import { eventsSessionStorage } from "services/storage/events";
import {
	requestCreateEvent,
	requestUpdateEventById,
} from "services/events/aboutEvents";
//#endregion
//#region Utils
import useMainContext from "utils/context/useMainContext";
import { TYPES } from "../../reducer/event.actions";
import validator from "../validations";
import { ALLOW_DURATION_EVENT_UPDATE } from "config/deploymentMode";
import { EVENTS_CONSTANTS } from "config/constants";
import useCheckNonExpiredDuration from "./useCheckNonExpiredDuration";
import { CERTIFICATION_SIGNATURE_SOURCE_STATUS } from "views/Eventos/utils/certification";
//#endregion

const { EVENT_PERIOD_IDS } = EVENTS_CONSTANTS;

const AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS = {
	signatures:
		"Completa la cantidad de firmas y configura cada slot requerido.",
	teacherSpeaker:
		"Completa o busca la información del docente / ponente.",
	validity:
		"Existe al menos una firma institucional sin vigencia válida para el campus organizador y la fecha fin del evento.",
	signatureCount: "Selecciona entre 1 y 3 firmas",
	campus:
		"Completa primero el campus organizador para configurar la generación automática de constancias.",
};

const DEFAULT_INVALID_STEP_MESSAGE =
	"Te faltan completar campos o existen campos inválidos.";

const useEventFormActions = ({
	loadingRequest,
	form, nextStep, toStep,
	teacherZoomAccountDetail,
	errors, changeErrorsStep,
	setMustCorrect, setMustCorrectMessage, setShowDialogUpdatedEvent,
	showToastMessageSaveEventAsDraft,
	changeErrorText, eventResumeData,
	isCompletedForm, setCanShowDialogLeavingPage,
	dialogShowErrorVacantCapacityForGreaterThanTheLimit,
	currentStep, showDialogSchedulesWithCrossings
}) => {
	const mainContext = useMainContext();
	const getStep2ValidationErrorMessage = newErrors => {
		if (newErrors.part4.autoCertificate.signatureCount)
			return AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.signatureCount;
		if (newErrors.part4.autoCertificate.duplicateTypes)
			return "No puedes repetir el mismo tipo de firma dentro de la misma configuración de constancias.";
		if (newErrors.part4.autoCertificate.invalidValidity)
			return AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.validity;
		if (newErrors.part4.autoCertificate.signatures)
			return AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.signatures;
		if (newErrors.part1.placeOrganization.campus)
			return AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.campus;
		if (newErrors.part1.teacher.free.accountId)
			return "Selecciona la cuenta que utilizará el docente / ponente para continuar.";
		if (newErrors.part1.teacher.search)
			return "Busca o selecciona correctamente al docente / ponente en 'Organizado por'.";
		if (newErrors.part1.teacher.free.fullName)
			return "Completa el nombre del docente / ponente en 'Organizado por'.";
		if (newErrors.part4.autoCertificate.signatureItems.some(item => item.fullName)) {
			const teacherSignatureStatuses =
				form.step2.part4.autoCertificate.signatures.map(
					signature => signature.sourceStatus
				);
			if (
				teacherSignatureStatuses.includes(
					CERTIFICATION_SIGNATURE_SOURCE_STATUS.AMBIGUOUS_EVENT_TEACHER_SIGNATURE
				)
			) {
				return "Existe más de una firma válida para el docente / ponente del evento en el campus organizador. Revisa la configuración en el administrador de firmas.";
			}
			if (
				teacherSignatureStatuses.includes(
					CERTIFICATION_SIGNATURE_SOURCE_STATUS.INVALID_EVENT_TEACHER_SIGNATURE
				)
			) {
				return "El docente / ponente debe tener una firma activa y vigente para el campus organizador y la fecha fin del evento.";
			}
			return AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.teacherSpeaker;
		}
		if (newErrors.part2.zoomTypeId)
			return "Selecciona el tipo de reunión de Zoom para continuar.";
		if (newErrors.part2.zoomInscriptionTypeId)
			return "Selecciona el tipo de inscripción de Zoom para continuar.";
		if (newErrors.part2.locationId)
			return "Selecciona la ubicación del evento para continuar.";
		if (newErrors.part3.quantityVacancy)
			return "Completa una cantidad de vacantes válida.";
		if (newErrors.part3.publicLoad.id)
			return "Completa a quiénes está dirigido el evento.";
		if (newErrors.part3.publicLoad.students.publicByCampusIds)
			return "Selecciona al menos un campus para el público del evento.";
		if (newErrors.part3.publicLoad.students.modalityId)
			return "Selecciona la modalidad del público objetivo.";
		if (newErrors.part3.publicLoad.list.studentsFile)
			return "Debes cargar el archivo de estudiantes para continuar.";
		return DEFAULT_INVALID_STEP_MESSAGE;
	};
	const applyCertificationBackendError = (result, certificationError) => {
		if (!certificationError) return false;
		const autoCertificateErrors = result.newErrors.part4.autoCertificate;
		switch (certificationError.scope) {
			case "campus":
				result.newErrors.part1.placeOrganization.campus = true;
				autoCertificateErrors.backendCampus = true;
				changeErrorText(
					"autoCertificateCampus",
					certificationError.text
				);
				break;
			case "signatureCount":
				autoCertificateErrors.signatureCount = true;
				changeErrorText(
					"autoCertificateSignatureCount",
					certificationError.text
				);
				break;
			case "duplicateTypes":
				autoCertificateErrors.duplicateTypes = true;
				break;
			case "teacherSpeaker":
				autoCertificateErrors.backendTeacherSpeaker = true;
				changeErrorText(
					"autoCertificateTeacherSpeaker",
					certificationError.text
				);
				break;
			case "institutionalValidity":
				autoCertificateErrors.invalidValidity = true;
				changeErrorText(
					"autoCertificateValidity",
					certificationError.text
				);
				break;
			case "signatures":
			default:
				autoCertificateErrors.signatures = true;
				changeErrorText(
					"autoCertificateSignatures",
					certificationError.text
				);
				break;
		}
		changeErrorsStep(TYPES.SET_FORM_ERRORS_STEP_2, result.newErrors);
		return false;
	};
	const fillErrorTeacher = (result, message) => {
		if (form.step2.part1.teacher.search.id) {
			result.newErrors.part1.teacher.search = true;
			changeErrorText("teacherSearch", message);
		}
		else {
			result.newErrors.part1.teacher.free.accountId = true;
			changeErrorText("teacherFreeAccount", message);
		}
		changeErrorsStep(TYPES.SET_FORM_ERRORS_STEP_2, result.newErrors);
	};
	function manageResultRequestSaveEvent(isValid) {
		if (typeof isValid === "undefined") return;
		if (isValid === null) {
			mainContext.setShowDialogUnexpectedError(true);
			return;
		}
		if (!isValid) {
			setMustCorrectMessage(
				prevMessage => prevMessage || DEFAULT_INVALID_STEP_MESSAGE
			);
			setMustCorrect(true);
			setTimeout(() => {
				setMustCorrect(false);
			}, 2400);
		} else {
			setMustCorrectMessage(DEFAULT_INVALID_STEP_MESSAGE);
			setCanShowDialogLeavingPage(false);
		}
		window.scrollTo({ top: 0, behavior: "smooth" });
	}
	function ignoreDurationFieldsInUpdateIfIsNotDraft() {
		if (ALLOW_DURATION_EVENT_UPDATE) return false;
		const isDraft = eventsSessionStorage.isDraftEvent.get();
		return {
			onlyDay:
				!isDraft &&
				eventResumeData.value?.step1.duration.period ===
					EVENT_PERIOD_IDS.MANY_DAYS,
			manyDays: !isDraft && eventResumeData.value?.step1.duration.period ===
					EVENT_PERIOD_IDS.MANY_DAYS,
		};
	}
	function manageErrorMessageOfStep1({ success, message }) {
		const {
			FORM_ACTION_ERROR_CODES,
			FORM_ERROR_TEXT_LIST_PER_FORM_ACTION_ERROR_CODE,
		} = EVENTS_CONSTANTS;
		switch (message) {
			case FORM_ACTION_ERROR_CODES.CROSS_SCHEDULE:
			case FORM_ACTION_ERROR_CODES.CROSS_SCHEDULE_PS:
			case FORM_ACTION_ERROR_CODES.CREATE_EVENT_ERROR_PS:
			case FORM_ACTION_ERROR_CODES.UPDATE_EVENT_PS:
			case FORM_ACTION_ERROR_CODES.NO_TEACHER_EMPLID_PS:  {
				const errorText = FORM_ERROR_TEXT_LIST_PER_FORM_ACTION_ERROR_CODE[message];
				mainContext.setDialogUnexpectedErrorMessage(errorText);
				return null;
			}
			default:
				if (!success) return null;
		}
		return true;
	}
	async function manageErrorMessageOfStep2(data, result) {
		const { success, message, schedulesWithCrossings, certificationError } =
			data;
		const {
			FORM_ACTION_ERROR_CODES,
			FORM_ERROR_TEXT_LIST_PER_FORM_ACTION_ERROR_CODE,
		} = EVENTS_CONSTANTS;
		if (certificationError) {
			applyCertificationBackendError(result, certificationError);
			return false;
		}
		switch (message) {
			case FORM_ACTION_ERROR_CODES.INVALID_CODE: {
				result.newErrors.part3.publicLoad.list.studentsFile = true;
				changeErrorText(
					"publicLoadStudentsFile",
					"Alguno de los códigos no existe, revísalo y súbelo nuevamente"
				);
				changeErrorsStep(
					TYPES.SET_FORM_ERRORS_STEP_2,
					result.newErrors
				);
				return false;
			}
			case FORM_ACTION_ERROR_CODES.NO_RECORDS: {
				result.newErrors.part3.publicLoad.list.studentsFile = true;
				changeErrorText(
					"publicLoadStudentsFile",
					"El archivo subido no contiene estudiantes para agregar, revísalo y súbelo nuevamente"
				);
				changeErrorsStep(
					TYPES.SET_FORM_ERRORS_STEP_2,
					result.newErrors
				);
				return false;
			}
			case FORM_ACTION_ERROR_CODES.INCORRECT_VACANT_CAPACITY: {
				dialogShowErrorVacantCapacityForGreaterThanTheLimit.toggle();
				return false;
			}
			case FORM_ACTION_ERROR_CODES.ACCOUNT_WEBINAR_CANNOT_UPDATE_ANOTHER_ACCOUNT_TYPE: {
				result.newErrors.part2.zoomTypeId = true;
				changeErrorText(
					"zoomTypeId",
					"Las cuentas webinar no pueden actualizarse a otro tipo de reunión o a otra cuenta."
				);
				changeErrorsStep(
					TYPES.SET_FORM_ERRORS_STEP_2,
					result.newErrors
				);
				return false;
			}
			case FORM_ACTION_ERROR_CODES.TEACHER_EMAIL_NOT_EXISTS:
			case FORM_ACTION_ERROR_CODES.INVALID_INVITATION_OF_ZOOM_ACCOUNT:
			case FORM_ACTION_ERROR_CODES.PENDING_INVITATION_OF_ZOOM_ACCOUNT:
			case FORM_ACTION_ERROR_CODES.LIMIT_EXCEEDED:
			case FORM_ACTION_ERROR_CODES.INACTIVE_TEACHER_ACCOUNT: {
				const errorText = FORM_ERROR_TEXT_LIST_PER_FORM_ACTION_ERROR_CODE[message];
				fillErrorTeacher(result, errorText);
				return false;
			}
			case FORM_ACTION_ERROR_CODES.UNEXPECTED_ERROR:
				return null;
			case FORM_ACTION_ERROR_CODES.CROSS_SCHEDULE:
			case FORM_ACTION_ERROR_CODES.CROSS_SCHEDULE_PS:
			case FORM_ACTION_ERROR_CODES.UPDATE_EVENT_PS:
			case FORM_ACTION_ERROR_CODES.NO_TEACHER_EMPLID_PS: {
				const errorText = FORM_ERROR_TEXT_LIST_PER_FORM_ACTION_ERROR_CODE[message];
				if (schedulesWithCrossings) {
					showDialogSchedulesWithCrossings(schedulesWithCrossings);
					// Se retorna undefined para cancelar la apertura del diálogo de error inesperado
					return undefined;
				}
				mainContext.setDialogUnexpectedErrorMessage(errorText);
				return null;
			}
			default:
				if (!success) return null;
		}
		return true;
	}
	const checkNonExpiredDuration = useCheckNonExpiredDuration({ form });
	const saveEventData = async loadingMessage => {
		loadingRequest.show(loadingMessage);
		setMustCorrectMessage(DEFAULT_INVALID_STEP_MESSAGE);
		const eventId = eventsSessionStorage.eventId.get();
		let isValid = null;
		let scrollToTop = true;
		switch (currentStep) {
			case 1: {
				const ignoreDurationFields = ignoreDurationFieldsInUpdateIfIsNotDraft();
				const result = validator.isValidFormStep1(
					form.step1,
					errors,
					ignoreDurationFields
				);
				changeErrorsStep(
					TYPES.SET_FORM_ERRORS_STEP_1,
					result.newErrors
				);
				isValid = result.isValid;
				if (!isValid) break;
				if (!checkNonExpiredDuration()) {
					loadingRequest.hide();
					isValid = undefined;
					return;
				}
				if (eventId) {
					const data = await requestUpdateEventById(
						eventId,
						form.step1,
						"step1",
						ignoreDurationFields
					);
					isValid = manageErrorMessageOfStep1(data);
				} else {
					const newEventId = await requestCreateEvent(form.step1);
					if (!newEventId) {
						isValid = null;
						break;
					}
					eventsSessionStorage.eventId.set(newEventId);
					eventsSessionStorage.isDraftEvent.set(true);
				}
				break;
			}
			case 2: {
				const result = validator.isValidFormStep2(form.step2, {
					teacherZoomAccountDetail,
					eventTypeId: form.step1.part2.eventTypeId,
				});
				changeErrorText(
					"publicLoadStudentsFile",
					"Es necesario que subas un archivo"
				);
				changeErrorText("teacherSearch", null);
				changeErrorText("teacherFreeAccount", "No has seleccionado una cuenta.");
				changeErrorText(
					"autoCertificateSignatures",
					AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.signatures
				);
				changeErrorText(
					"autoCertificateTeacherSpeaker",
					AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.teacherSpeaker
				);
				changeErrorText(
					"autoCertificateValidity",
					AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.validity
				);
				changeErrorText(
					"autoCertificateSignatureCount",
					AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.signatureCount
				);
				changeErrorText(
					"autoCertificateCampus",
					AUTO_CERTIFICATE_ERROR_TEXT_DEFAULTS.campus
				);
				changeErrorsStep(
					TYPES.SET_FORM_ERRORS_STEP_2,
					result.newErrors
				);
				isValid = result.isValid;
				if (!isValid) {
					setMustCorrectMessage(
						getStep2ValidationErrorMessage(result.newErrors)
					);
					break;
				}
				const data = await requestUpdateEventById(
					eventId,
					form.step2,
					"step2"
				);
				if (
					data.message ===
					EVENTS_CONSTANTS.FORM_ACTION_ERROR_CODES
						.INCORRECT_VACANT_CAPACITY
				)
					scrollToTop = false;
				isValid = await manageErrorMessageOfStep2(data, result);
				break;
			}
			default:
				return false;
		}
		if (scrollToTop) manageResultRequestSaveEvent(isValid);
		loadingRequest.hide();
		return isValid;
	};
	const saveAndContinue = async () => {
		const isValid = await saveEventData("Guardando...");
		if (!isValid) return;
		const isCompleted = isCompletedForm(form);
		if (isCompleted.step2) {
			toStep(3);
			await eventResumeData.fill();
		} else if (isCompleted.step1) toStep(2);
		showToastMessageSaveEventAsDraft();
	};
	const saveEventAsDraft = async () => {
		const isValid = await saveEventData("Guardando...");
		if (!isValid) return;
		showToastMessageSaveEventAsDraft();
	};
	const updateEventStep = async () => {
		const isValid = await saveEventData("Actualizando tu evento creado...");
		if (!isValid) return;
		if (eventsSessionStorage.isDraftEvent.get()) {
			nextStep();
			currentStep === 1 && nextStep();
			return;
		}
		setShowDialogUpdatedEvent(true);
	};
	return {
		saveAndContinue,
		saveEventAsDraft,
		updateEventStep,
	};
};

export default useEventFormActions;
